import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ref, get, set, remove, push } from 'firebase/database';
import { db } from '../../config/firebase';
import QRCode from 'qrcode';
import Modal from '../ui/Modal';
import {
  WorkspaceEmptyState,
  WorkspacePageLoader,
} from '../ui/Workspace';

const PROGRAM_LEVELS = [
  { value: 'diploma', label: 'Diploma' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'advanced-diploma', label: 'Advanced Diploma' },
];

function rowsFromStudents(students) {
  if (!students) return [{ _fbKey: null, id: '', name: '', specialAchievements: '' }];
  if (Array.isArray(students)) {
    return students.length
      ? students.map((s, i) => ({
          _fbKey: `legacy_${i}`,
          id: s.id ?? '',
          name: s.name ?? '',
          specialAchievements: s.specialAchievements ?? '',
        }))
      : [{ _fbKey: null, id: '', name: '', specialAchievements: '' }];
  }
  return Object.entries(students).map(([key, s]) => ({
    _fbKey: key,
    id: s.id ?? '',
    name: s.name ?? '',
    specialAchievements: s.specialAchievements ?? '',
  }));
}

function parseBulkStudentLines(text) {
  const lines = text.split(/\r?\n/);
  const out = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    let id = '';
    let name = '';
    const tabIdx = trimmed.indexOf('\t');
    if (tabIdx !== -1) {
      id = trimmed.slice(0, tabIdx).trim();
      name = trimmed.slice(tabIdx + 1).replace(/\t/g, ' ').trim();
    } else {
      const spaced = trimmed.match(/^(.+?)\s{2,}(.+)$/);
      if (spaced) {
        id = spaced[1].trim();
        name = spaced[2].trim();
      } else {
        name = trimmed;
      }
    }
    if (!name) continue;
    out.push({ _fbKey: null, id: id.slice(0, 20), name: name.slice(0, 100), specialAchievements: '' });
  }
  return out;
}

function buildStudentsObject(rows) {
  const out = {};
  rows.forEach((row) => {
    const name = (row.name || '').trim();
    if (!name) return;
    const key =
      row._fbKey && !String(row._fbKey).startsWith('legacy_')
        ? row._fbKey
        : `s_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const entry = { id: (row.id || '').trim().slice(0, 20), name: name.slice(0, 100) };
    const ach = (row.specialAchievements || '').trim();
    if (ach) entry.specialAchievements = ach.slice(0, 300);
    out[key] = entry;
  });
  return out;
}

function batchVerificationPageUrl(batchKey) {
  return `${window.location.origin}/verification?batch=${encodeURIComponent(batchKey)}`;
}

const VerificationManagement = () => {
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('list');
  const [editingKey, setEditingKey] = useState(null);

  const [programName, setProgramName] = useState('');
  const [year, setYear] = useState('');
  const [batchNum, setBatchNum] = useState(1);
  const [programLevel, setProgramLevel] = useState('diploma');
  const [metadataProgramLevel, setMetadataProgramLevel] = useState('');
  const [certificationBody, setCertificationBody] = useState('');
  const [completionDate, setCompletionDate] = useState('');
  const [studentRows, setStudentRows] = useState([
    { _fbKey: null, id: '', name: '', specialAchievements: '' },
  ]);
  const [bulkPasteText, setBulkPasteText] = useState('');
  const [bulkPasteNotice, setBulkPasteNotice] = useState('');

  const [linkModal, setLinkModal] = useState(null);
  const [modalQrDataUrl, setModalQrDataUrl] = useState('');
  const [modalQrLoading, setModalQrLoading] = useState(false);
  const [copyUrlDone, setCopyUrlDone] = useState(false);

  const [qrDataUrl, setQrDataUrl] = useState('');
  const [batchUrl, setBatchUrl] = useState('');

  const activeBatchKey = editingKey;

  const refreshList = useCallback(async () => {
    setListLoading(true);
    setError(null);
    try {
      const snap = await get(ref(db, 'verification'));
      const val = snap.val() || {};
      const rows = Object.entries(val).map(([key, b]) => ({
        key,
        programName: b.programName,
        year: b.year,
        batch: b.batch,
        programLevel: b.programLevel,
        totalStudents:
          b.metadata?.totalStudents ??
          (Array.isArray(b.students) ? b.students.length : Object.keys(b.students || {}).length),
      }));
      rows.sort((a, b) => String(b.year).localeCompare(String(a.year)));
      setList(rows);
    } catch (e) {
      console.error(e);
      setError('Could not load verification batches.');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => { refreshList(); }, [refreshList]);

  const rebuildQr = useCallback(async (key) => {
    if (!key) { setQrDataUrl(''); setBatchUrl(''); return; }
    const url = batchVerificationPageUrl(key);
    setBatchUrl(url);
    try {
      const dataUrl = await QRCode.toDataURL(url, {
        width: 280, margin: 2, color: { dark: '#111827', light: '#ffffff' },
      });
      setQrDataUrl(dataUrl);
    } catch (e) { console.error(e); setQrDataUrl(''); }
  }, []);

  useEffect(() => {
    if (mode === 'form' && activeBatchKey) rebuildQr(activeBatchKey);
    else { setQrDataUrl(''); setBatchUrl(''); }
  }, [mode, activeBatchKey, rebuildQr]);

  useEffect(() => {
    if (!linkModal) { setModalQrDataUrl(''); setModalQrLoading(false); return; }
    const url = batchVerificationPageUrl(linkModal.key);
    setModalQrLoading(true);
    setModalQrDataUrl('');
    QRCode.toDataURL(url, { width: 256, margin: 2, color: { dark: '#111827', light: '#ffffff' } })
      .then((d) => setModalQrDataUrl(d))
      .catch(() => setModalQrDataUrl(''))
      .finally(() => setModalQrLoading(false));
  }, [linkModal]);

  const resetForm = () => {
    setProgramName(''); setYear(''); setBatchNum(1); setProgramLevel('diploma');
    setMetadataProgramLevel(''); setCertificationBody(''); setCompletionDate('');
    setStudentRows([{ _fbKey: null, id: '', name: '', specialAchievements: '' }]);
    setEditingKey(null);
  };

  const startCreate = () => {
    setLinkModal(null); setError(null); resetForm();
    const newKey = push(ref(db, 'verification')).key;
    setEditingKey(newKey); setMode('form'); rebuildQr(newKey);
  };

  const startEdit = async (key) => {
    setLinkModal(null); setFormLoading(true); setError(null);
    try {
      const snap = await get(ref(db, `verification/${key}`));
      const b = snap.val();
      if (!b) { setError('Batch not found.'); return; }
      setEditingKey(key);
      setProgramName(b.programName || ''); setYear(b.year || '');
      setBatchNum(Number(b.batch) || 1); setProgramLevel(b.programLevel || 'diploma');
      setMetadataProgramLevel(b.metadata?.programLevel || '');
      setCertificationBody(b.metadata?.certificationBody || '');
      setCompletionDate(b.metadata?.completionDate || '');
      setStudentRows(rowsFromStudents(b.students)); setMode('form');
    } catch (e) { console.error(e); setError('Failed to open batch for editing.'); }
    finally { setFormLoading(false); }
  };

  const saveBatch = async () => {
    const studentsObj = buildStudentsObject(studentRows);
    const total = Object.keys(studentsObj).length;
    if (!programName.trim()) { setError('Program name is required.'); return; }
    if (!year.trim()) { setError('Year is required.'); return; }
    if (!completionDate || !/^\d{4}-\d{2}-\d{2}$/.test(completionDate)) {
      setError('Completion date must be YYYY-MM-DD.'); return;
    }
    if (!certificationBody.trim()) { setError('Certification body is required.'); return; }
    if (!metadataProgramLevel.trim()) { setError('Display program level is required.'); return; }
    if (total < 1) { setError('Add at least one student with a name.'); return; }
    if (!editingKey) { setError('Missing batch id. Go back and try again.'); return; }
    setSaving(true); setError(null);
    const payload = {
      programName: programName.trim().slice(0, 200),
      year: year.trim().slice(0, 10),
      batch: Number(batchNum),
      programLevel,
      students: studentsObj,
      metadata: {
        totalStudents: total,
        completionDate,
        certificationBody: certificationBody.trim().slice(0, 100),
        programLevel: metadataProgramLevel.trim().slice(0, 100),
      },
    };
    try {
      await set(ref(db, `verification/${editingKey}`), payload);
      await refreshList(); setMode('list'); resetForm();
    } catch (e) { console.error(e); setError(e.message || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const deleteBatch = async (key) => {
    if (!window.confirm('Delete this batch and all listed students? This cannot be undone.')) return;
    try { await remove(ref(db, `verification/${key}`)); await refreshList(); }
    catch (e) { console.error(e); setError('Failed to delete batch.'); }
  };

  const addStudentRow = () =>
    setStudentRows((prev) => [...prev, { _fbKey: null, id: '', name: '', specialAchievements: '' }]);

  const updateStudentRow = (index, field, value) =>
    setStudentRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });

  const removeStudentRow = (index) =>
    setStudentRows((prev) => prev.filter((_, i) => i !== index));

  const applyBulkPaste = (replaceExisting) => {
    setBulkPasteNotice('');
    const parsed = parseBulkStudentLines(bulkPasteText);
    if (parsed.length === 0) {
      setBulkPasteNotice('No rows added. Use a tab between student ID and name, or two or more spaces.');
      return;
    }
    setStudentRows((prev) => {
      if (replaceExisting) return parsed;
      const meaningful = prev.filter((r) => (r.name || '').trim() || (r.id || '').trim());
      return meaningful.length === 0 && prev.length === 1 ? parsed : [...meaningful, ...parsed];
    });
    setBulkPasteText('');
    setBulkPasteNotice(`Added ${parsed.length} student(s) to the table.`);
  };

  const downloadQr = () => {
    if (!qrDataUrl || !editingKey) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `certificate-qr-${editingKey.slice(0, 24)}.png`;
    a.click();
  };

  const openBatchLinkModal = (row) => {
    setCopyUrlDone(false);
    setLinkModal({ key: row.key, programName: row.programName });
  };

  const copyModalBatchUrl = async () => {
    if (!linkModal) return;
    try {
      await navigator.clipboard.writeText(batchVerificationPageUrl(linkModal.key));
      setCopyUrlDone(true);
      setTimeout(() => setCopyUrlDone(false), 2000);
    } catch { setError('Could not copy URL. Copy it manually from the dialog.'); }
  };

  const downloadModalQr = () => {
    if (!modalQrDataUrl || !linkModal) return;
    const a = document.createElement('a');
    a.href = modalQrDataUrl;
    a.download = `certificate-qr-${linkModal.key.slice(0, 24)}.png`;
    a.click();
  };

  const publicLink = useMemo(() => (editingKey ? batchVerificationPageUrl(editingKey) : ''), [editingKey]);

  /* ── QR & Link modal ─────────────────────────────────────────── */
  const batchLinkModalMarkup = linkModal ? (
    <Modal
      isOpen
      onClose={() => setLinkModal(null)}
      panelClassName="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full w-full max-w-2xl"
    >
      <div className="flex items-start justify-between gap-4 border-b border-stone-200 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-stone-950">Verification link & QR</h2>
          <p className="mt-0.5 text-sm text-stone-500">{linkModal.programName}</p>
        </div>
        <button
          type="button"
          onClick={() => setLinkModal(null)}
          className="text-stone-400 hover:text-stone-700 text-2xl leading-none"
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div className="px-5 py-5">
        <div className="flex flex-col gap-5 sm:flex-row">
          <div className="flex shrink-0 justify-center sm:justify-start">
            {modalQrLoading ? (
              <div className="flex h-64 w-64 items-center justify-center rounded-lg border border-stone-200 bg-stone-50 text-sm text-stone-500">
                Generating…
              </div>
            ) : modalQrDataUrl ? (
              <img
                src={modalQrDataUrl}
                alt="Verification QR code"
                className="h-64 w-64 rounded-lg border border-stone-200 bg-white object-contain"
              />
            ) : (
              <div className="flex h-64 w-64 items-center justify-center rounded-lg border border-red-100 bg-red-50 px-4 text-center text-sm text-red-700">
                Could not generate QR.
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1 space-y-4">
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                Page URL
              </p>
              <input
                readOnly
                className="ledger-input min-h-[38px] font-mono text-xs"
                value={batchVerificationPageUrl(linkModal.key)}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={copyModalBatchUrl}
                className="rounded-md bg-stone-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-stone-800"
              >
                {copyUrlDone ? 'Copied!' : 'Copy URL'}
              </button>
              <a
                href={batchVerificationPageUrl(linkModal.key)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-md border border-stone-200 bg-white px-4 py-2 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
              >
                Open page
              </a>
              <button
                type="button"
                onClick={downloadModalQr}
                disabled={!modalQrDataUrl}
                className="rounded-md border border-stone-200 bg-white px-4 py-2 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 disabled:opacity-50"
              >
                Download QR
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  ) : null;

  /* ── Loading ─────────────────────────────────────────────────── */
  if (listLoading && mode === 'list') return <WorkspacePageLoader />;

  /* ── Form view ───────────────────────────────────────────────── */
  if (mode === 'form') {
    if (formLoading) return <WorkspacePageLoader />;
    return (
      <>
        {batchLinkModalMarkup}
        <div className="mx-auto w-full max-w-[min(100%,96rem)] space-y-3 py-1">
          {/* Header */}
          <header className="rounded-lg border border-stone-200 bg-white px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500">
                  Workspace
                </p>
                <h1 className="mt-0.5 text-2xl font-semibold tracking-tight text-stone-950">
                  {list.some((r) => r.key === editingKey) ? 'Edit Batch' : 'New Passed-out Batch'}
                </h1>
              </div>
              <button
                type="button"
                onClick={() => { setLinkModal(null); setMode('list'); resetForm(); }}
                className="rounded-md border border-stone-200 bg-white px-4 py-2 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
              >
                ← Back to list
              </button>
            </div>
          </header>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Program details */}
          <section className="rounded-lg border border-stone-200 bg-white">
            <div className="border-b border-stone-200 px-4 py-3">
              <h2 className="text-base font-semibold tracking-tight text-stone-950">Program details</h2>
              <p className="text-xs text-stone-500">Basic fields for program, year, batch, and certification.</p>
            </div>
            <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <VerifField label="Program name">
                  <input
                    type="text"
                    value={programName}
                    onChange={(e) => setProgramName(e.target.value)}
                    className="ledger-input min-h-[38px]"
                    placeholder="e.g. Diploma in Beauty Culture (2024/2025)"
                  />
                </VerifField>
              </div>
              <VerifField label="Year">
                <input
                  type="text"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="ledger-input min-h-[38px]"
                  placeholder="2024/2025"
                />
              </VerifField>
              <VerifField label="Batch number">
                <input
                  type="number"
                  min={1}
                  value={batchNum}
                  onChange={(e) => setBatchNum(Number(e.target.value) || 1)}
                  className="ledger-input min-h-[38px]"
                />
              </VerifField>
              <VerifField label="Program level">
                <select
                  value={programLevel}
                  onChange={(e) => setProgramLevel(e.target.value)}
                  className="ledger-input min-h-[38px]"
                >
                  {PROGRAM_LEVELS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </VerifField>
              <VerifField label="Certification body">
                <input
                  type="text"
                  value={certificationBody}
                  onChange={(e) => setCertificationBody(e.target.value)}
                  className="ledger-input min-h-[38px]"
                  placeholder="e.g. NAITA"
                />
              </VerifField>
              <VerifField label="Completion date (YYYY-MM-DD)">
                <input
                  type="date"
                  value={completionDate}
                  onChange={(e) => setCompletionDate(e.target.value)}
                  className="ledger-input min-h-[38px]"
                />
              </VerifField>
              <VerifField label="Display level (shown on public page)">
                <input
                  type="text"
                  value={metadataProgramLevel}
                  onChange={(e) => setMetadataProgramLevel(e.target.value)}
                  className="ledger-input min-h-[38px]"
                  placeholder="e.g. NVQ Level 4"
                />
              </VerifField>
            </div>
          </section>

          {/* Students */}
          <section className="rounded-lg border border-stone-200 bg-white">
            <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
              <div>
                <h2 className="text-base font-semibold tracking-tight text-stone-950">Students</h2>
                <p className="text-xs text-stone-500">
                  {studentRows.filter((r) => (r.name || '').trim()).length} student
                  {studentRows.filter((r) => (r.name || '').trim()).length !== 1 ? 's' : ''} added
                </p>
              </div>
              <button
                type="button"
                onClick={addStudentRow}
                className="rounded-md border border-stone-200 bg-white px-3 py-1.5 text-xs font-semibold text-stone-700 transition hover:bg-stone-50"
              >
                Add row
              </button>
            </div>

            {/* Bulk paste */}
            <div className="border-b border-stone-200 bg-stone-50 px-4 py-3">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                Bulk paste
              </p>
              <p className="mb-2 text-xs text-stone-600">
                Paste from Excel or text: <strong>student ID</strong> then a <strong>tab</strong> then <strong>full name</strong>. Two or more spaces also work as a separator.
              </p>
              <textarea
                value={bulkPasteText}
                onChange={(e) => { setBulkPasteText(e.target.value); setBulkPasteNotice(''); }}
                rows={4}
                spellCheck={false}
                className="ledger-input min-h-[80px] font-mono text-xs"
                placeholder={'Jan Batch\tN W K Rishmi Awanthi\nB13.0620\tP.P.S.W.Pathirana'}
              />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => applyBulkPaste(false)}
                  className="rounded-md bg-stone-950 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-stone-800"
                >
                  Add to table
                </button>
                <button
                  type="button"
                  onClick={() => applyBulkPaste(true)}
                  className="rounded-md border border-stone-200 bg-white px-3 py-1.5 text-xs font-semibold text-stone-700 transition hover:bg-stone-50"
                >
                  Replace table with paste
                </button>
                <button
                  type="button"
                  onClick={() => { setBulkPasteText(''); setBulkPasteNotice(''); }}
                  className="text-xs font-semibold text-stone-500 hover:text-stone-700"
                >
                  Clear
                </button>
              </div>
              {bulkPasteNotice && (
                <p className="mt-2 rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-800">
                  {bulkPasteNotice}
                </p>
              )}
            </div>

            {/* Student table */}
            <div className="overflow-x-auto">
              <table className="min-w-full border-separate border-spacing-0 text-sm">
                <thead>
                  <tr>
                    {['Student ID', 'Name', 'Special achievements', ''].map((col) => (
                      <th
                        key={col}
                        className="border-b border-stone-200 bg-stone-50 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {studentRows.map((row, i) => (
                    <tr key={i} className="group">
                      <td className="border-b border-stone-100 px-3 py-1.5 group-hover:bg-stone-50">
                        <input
                          value={row.id}
                          onChange={(e) => updateStudentRow(i, 'id', e.target.value)}
                          className="ledger-input min-h-[32px] min-w-[8rem] rounded-md px-2 text-xs"
                          placeholder="Optional"
                          maxLength={20}
                        />
                      </td>
                      <td className="border-b border-stone-100 px-3 py-1.5 group-hover:bg-stone-50">
                        <input
                          value={row.name}
                          onChange={(e) => updateStudentRow(i, 'name', e.target.value)}
                          className="ledger-input min-h-[32px] min-w-[12rem] rounded-md px-2 text-xs"
                          placeholder="Full name"
                        />
                      </td>
                      <td className="border-b border-stone-100 px-3 py-1.5 group-hover:bg-stone-50">
                        <input
                          value={row.specialAchievements}
                          onChange={(e) => updateStudentRow(i, 'specialAchievements', e.target.value)}
                          className="ledger-input min-h-[32px] min-w-[10rem] rounded-md px-2 text-xs"
                          placeholder="Optional"
                        />
                      </td>
                      <td className="border-b border-stone-100 px-3 py-1.5 text-right group-hover:bg-stone-50">
                        <button
                          type="button"
                          onClick={() => removeStudentRow(i)}
                          disabled={studentRows.length <= 1}
                          className="text-xs font-semibold text-red-600 hover:text-red-900 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="border-t border-stone-100 px-4 py-2 text-xs text-stone-500">
              Empty name rows are skipped when saving.
            </p>

            {/* Save */}
            <div className="flex flex-wrap gap-2 border-t border-stone-200 px-4 py-3">
              <button
                type="button"
                onClick={saveBatch}
                disabled={saving}
                className="rounded-md bg-stone-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-stone-800 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save batch'}
              </button>
            </div>
          </section>

          {/* QR Code */}
          <section className="rounded-lg border border-stone-200 bg-white">
            <div className="border-b border-stone-200 px-4 py-3">
              <h2 className="text-base font-semibold tracking-tight text-stone-950">Certificate QR code</h2>
              <p className="text-xs text-stone-500">
                Scanning opens the public verification page for this batch. The link is fixed as soon as you create the batch.
              </p>
            </div>
            <div className="flex flex-col gap-5 p-4 sm:flex-row sm:items-start">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="Batch verification QR"
                  className="rounded-lg border border-stone-200"
                />
              ) : (
                <div className="flex h-[280px] w-[280px] items-center justify-center rounded-lg border border-stone-200 bg-stone-50 text-sm text-stone-500">
                  Generating…
                </div>
              )}
              <div className="min-w-0 flex-1 space-y-3">
                <div>
                  <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-stone-500">Batch key</p>
                  <p className="break-all font-mono text-sm text-stone-800">{editingKey}</p>
                </div>
                <div>
                  <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-stone-500">URL</p>
                  <p className="break-all text-sm text-[#b91c66]">{batchUrl || publicLink}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={downloadQr}
                    disabled={!qrDataUrl}
                    className="rounded-md bg-stone-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-stone-800 disabled:opacity-50"
                  >
                    Download QR (PNG)
                  </button>
                  <a
                    href={batchUrl || publicLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-md border border-stone-200 bg-white px-4 py-2 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
                  >
                    Open public page
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </>
    );
  }

  /* ── List view ───────────────────────────────────────────────── */
  return (
    <>
      {batchLinkModalMarkup}
      <div className="mx-auto w-full max-w-[min(100%,96rem)] space-y-3 py-1">
        {/* Header */}
        <header className="rounded-lg border border-stone-200 bg-white px-4 py-3">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500">
                Workspace
              </p>
              <h1 className="mt-0.5 text-2xl font-semibold tracking-tight text-stone-950">
                Verifications
              </h1>
            </div>
            <button
              type="button"
              onClick={startCreate}
              className="rounded-md bg-stone-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-stone-800"
            >
              New passed-out batch
            </button>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <CourseStat label="Total Batches" value={list.length} />
            <CourseStat label="Students Listed" value={list.reduce((s, r) => s + (r.totalStudents || 0), 0)} />
          </div>
        </header>

        <p className="text-sm text-stone-600">
          Manage course completion listings shown on the{' '}
          <Link
            to="/verification"
            className="font-medium text-[#b91c66] hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            public verification page
          </Link>
          . Use <strong>QR &amp; link</strong> to copy the URL or download the certificate QR code.
        </p>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <section className="rounded-lg border border-stone-200 bg-white">
          <div className="border-b border-stone-200 px-3 py-2">
            <h2 className="text-base font-semibold tracking-tight text-stone-950">Passed-out batches</h2>
            <p className="text-xs text-stone-500">All registered verification batches, sorted newest first.</p>
          </div>
          <div className="p-2">
            {list.length === 0 ? (
              <WorkspaceEmptyState
                title="No batches yet"
                message='Click "New passed-out batch" to create the first verification record.'
              />
            ) : (
              <div className="overflow-x-auto rounded-md border border-stone-200">
                <table className="min-w-full border-separate border-spacing-0 text-sm">
                  <thead>
                    <tr>
                      {['Program', 'Year', 'Batch', 'Level', 'Students', ''].map((col) => (
                        <th
                          key={col}
                          className="border-b border-stone-200 bg-stone-50 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((row) => (
                      <tr key={row.key} className="group">
                        <td className="border-b border-stone-100 px-3 py-2.5 font-semibold text-stone-950 group-hover:bg-stone-50">
                          {row.programName}
                        </td>
                        <td className="border-b border-stone-100 px-3 py-2.5 text-stone-700 group-hover:bg-stone-50">
                          {row.year}
                        </td>
                        <td className="border-b border-stone-100 px-3 py-2.5 text-stone-700 group-hover:bg-stone-50">
                          {row.batch}
                        </td>
                        <td className="border-b border-stone-100 px-3 py-2.5 group-hover:bg-stone-50">
                          <span className="inline-flex rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold capitalize text-stone-700 ring-1 ring-stone-200">
                            {row.programLevel?.replace(/-/g, ' ') || '—'}
                          </span>
                        </td>
                        <td className="border-b border-stone-100 px-3 py-2.5 text-stone-700 group-hover:bg-stone-50">
                          {row.totalStudents}
                        </td>
                        <td className="border-b border-stone-100 px-3 py-2.5 text-right group-hover:bg-stone-50">
                          <div className="flex justify-end gap-3">
                            <button
                              type="button"
                              onClick={() => startEdit(row.key)}
                              className="text-xs font-semibold text-[#b91c66] hover:text-[#9d174d]"
                            >
                              Edit
                            </button>
                            <a
                              href={batchVerificationPageUrl(row.key)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-semibold text-[#b91c66] hover:text-[#9d174d]"
                            >
                              View page
                            </a>
                            <button
                              type="button"
                              onClick={() => openBatchLinkModal(row)}
                              className="text-xs font-semibold text-stone-600 hover:text-stone-900"
                            >
                              QR &amp; link
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteBatch(row.key)}
                              className="text-xs font-semibold text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

const VerifField = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">
      {label}
    </span>
    {children}
  </label>
);

const CourseStat = ({ label, value }) => (
  <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</p>
    <p className="mt-0.5 truncate text-base font-semibold text-stone-950">{value}</p>
  </div>
);

export default VerificationManagement;
